/**
 * Centralized access to Vite-injected environment variables.
 *
 * Falls back to QLASH staging defaults so the dev server runs out of the box
 * without requiring a manual .env.local. Override any value by copying
 * .env.example to .env.local and editing.
 */

import { resolveLimuloApiUrl } from '@/lib/limulo/config';

const orDefault = (value: string | undefined, fallback: string): string =>
  value && value.length > 0 ? value : fallback;

// RTK Query talks to the Laravel monolith via the SAME-ORIGIN proxy path
// `/qlash-api/*` in both DEV and PROD — the monolith's CORS middleware is
// misconfigured (supports_credentials: true + allowed_origins: ['*'] is
// invalid per the CORS spec, so no Access-Control-* headers are emitted)
// and a cross-origin request from the browser is blocked. The proxy is
// resolved server-side: Vite dev server in DEV, CloudFront behavior on
// the PROD-TmaStack distribution in PROD (both forward to VITE_API_URL).
//
// Other backends (qnews, qavatar, qriots, qbot, v2-api) have proper CORS
// and stay direct — only this one needs proxying.
const isDev = import.meta.env.DEV;

export const env = {
  apiUrl: '/qlash-api',
  newsApiUrl: isDev
    ? '/qnews-api'
    : orDefault(import.meta.env.VITE_NEWS_API_URL as string | undefined, 'https://qnews.qlash.gg'),
  avatarApiUrl: isDev
    ? '/qavatar-api'
    : orDefault(
        import.meta.env.VITE_AVATAR_API_URL as string | undefined,
        'https://qavatar.qlash.gg',
      ),
  qriotsApiUrl: isDev
    ? '/qriots-api'
    : orDefault(
        import.meta.env.VITE_QRIOTS_API_URL as string | undefined,
        'https://qriots.qlash.gg',
      ),
  // QBot — Clash Royale data + AI coaching. Same Logto API resource as the
  // backoffice (https://qbot.qlash.gg) so no new Logto registration needed.
  qbotApiUrl: isDev
    ? '/qbot-api'
    : orDefault(import.meta.env.VITE_QBOT_API_URL as string | undefined, 'https://qbot.qlash.gg'),
  // QSocials — friend/follow + presence + co-watching + cheers. Wave 1 ships
  // Telegram-only. Audience: `https://qsocials.qlash.gg` (separate Logto API
  // resource from the Laravel monolith). In dev proxied through Vite to
  // bypass CORS — same pattern as qriots/qbot.
  qsocialsApiUrl: isDev
    ? '/qsocials-api'
    : orDefault(
        import.meta.env.VITE_QSOCIALS_URL as string | undefined,
        'https://qsocials.qlash.gg',
      ),
  // QDuels — 1v1 Clash Royale duel challenges sent from the friends list.
  // Dedicated Logto API resource: `https://qduels.qlash.gg/api` (note the
  // `/api` suffix in the audience; the base URL itself has no suffix). In dev
  // proxied through Vite to bypass CORS — same pattern as qriots/qbot/qsocials.
  qduelsApiUrl: isDev
    ? '/qduels-api'
    : orDefault(
        import.meta.env.VITE_QDUELS_API_URL as string | undefined,
        'https://qduels.qlash.gg',
      ),
  // QChess — chess tournaments + 1v1 duels hosted on Lichess, with a QLASH
  // leaderboard. Dedicated Logto API resource: `https://qchess.qlash.gg/api`
  // (the `/api`-suffixed audience convention from qriots/qduels). In dev
  // proxied through Vite to bypass CORS — same pattern as the other backends.
  // Dev target is the MacMini over Tailscale (`http://100.94.221.52:3600`).
  qchessApiUrl: isDev
    ? '/qchess-api'
    : orDefault(
        import.meta.env.VITE_QCHESS_API_URL as string | undefined,
        'https://qchess.qlash.gg',
      ),
  // QClans — Clash of Clans vertical (stats, clan-war/CWL/Capital tracking,
  // QLASH competitions & leaderboards, AI coaching). Telegram-first. Dedicated
  // Logto API resource: `https://qclans.qlash.gg/api` (the `/api`-suffixed
  // audience convention from qriots/qduels/qchess; the base URL has no suffix).
  // In dev proxied through Vite to bypass CORS — same pattern as the other
  // backends. Dev target is the MacMini over Tailscale (`http://100.94.221.52:3700`).
  qclansApiUrl: isDev
    ? '/qclans-api'
    : orDefault(
        import.meta.env.VITE_QCLANS_API_URL as string | undefined,
        'https://qclans.qlash.gg',
      ),
  // QClans dev-bypass token. qclans-api accepts `X-Dev-Bypass: <token>` (+
  // optional `X-Dev-User`) only when it runs NODE_ENV=development — a local
  // testing escape hatch for before the "Qlash API QClans" Logto resource is
  // registered + granted (a P6/deploy task). When set (dev only), qclansApi
  // attaches it AND skips the Logto Bearer path. Never set in prod builds; the
  // value is public in the bundle, so the API must keep it dev-gated server-side.
  qclansDevBypass: isDev
    ? orDefault(import.meta.env.VITE_QCLANS_DEV_BYPASS as string | undefined, '')
    : '',
  qclansDevUser: isDev
    ? orDefault(import.meta.env.VITE_QCLANS_DEV_USER as string | undefined, '')
    : '',
  // QGammon — real-time multiplayer backgammon. REST (leaderboard/history) on
  // qgammon-api; live play on the qgammon-gameserver (Socket.IO). PROD lives on
  // the OVH box since 2026-07-25: https://qgammon.qlash.gg (REST) +
  // wss://qgammon-ws.qlash.gg (Socket.IO) — the MacMini-era Tailscale defaults
  // (100.94.221.52:4000/:4001) are gone; that instance is retired for dev use.
  // Dedicated Logto resource `https://qgammon.qlash.gg/api`. What matters here is
  // not whether it is granted somewhere, but what a user WITHOUT scopes on it
  // experiences: the token their roles yield is unusable by this surface, and the
  // app does not fall back to X-Dev-Bypass while a session exists (the
  // `!hasStoredSession()` guard in lib/qgammon/auth.ts) — so calls go out
  // unauthenticated and are refused ON THE SOCKET, with nothing on screen. That
  // is why the surface is limited by `<RoleGate role="beta">`, not by the flag. ⚠️ The OVH deployment runs NODE_ENV=production and
  // has DEV_BYPASS_TOKEN removed (security gate 2026-07-25), so X-Dev-Bypass
  // does NOT work against these defaults — it only works against a local
  // NODE_ENV=development QGammon (override VITE_QGAMMON_API_URL/_WS_URL + set
  // VITE_QGAMMON_DEV_BYPASS in .env.development.local for that).
  qgammonApiUrl: isDev
    ? '/qgammon-api'
    : orDefault(
        import.meta.env.VITE_QGAMMON_API_URL as string | undefined,
        'https://qgammon.qlash.gg',
      ),
  // Socket.IO base for live play (used by the match board — Fase 6 part 2).
  qgammonWsUrl: orDefault(
    import.meta.env.VITE_QGAMMON_WS_URL as string | undefined,
    'wss://qgammon-ws.qlash.gg',
  ),
  qgammonDevBypass: isDev
    ? orDefault(import.meta.env.VITE_QGAMMON_DEV_BYPASS as string | undefined, '')
    : '',
  qgammonDevUser: isDev
    ? orDefault(import.meta.env.VITE_QGAMMON_DEV_USER as string | undefined, '')
    : '',
  // QChinesePoker (QCP) — heads-up Pineapple OFC + Fantasyland. REST
  // (leaderboard/history/challenge management) on qcp-api :4100; live play +
  // friend-invite endpoints on qcp-gameserver Socket.IO :4101. Invites share
  // the gameserver origin (HTTP) because they are co-located there — same host
  // as the Socket.IO connection. Dedicated Logto resource TBD (use X-Dev-Bypass
  // meanwhile). Dev proxies REST through Vite to the MacMini over Tailscale
  // (`http://100.94.221.52:4100`); gameserver :4101 connects directly (no proxy).
  qcpApiUrl: isDev
    ? '/qchinesepoker-api'
    : orDefault(
        import.meta.env.VITE_QCP_API_URL as string | undefined,
        'https://qchinesepoker.qlash.gg',
      ),
  // Socket.IO base for live play (gameserver). Also the HTTP origin for the
  // friend-invite REST endpoints (POST /invites etc.) which are served by the
  // same Fastify instance as the Socket.IO server on :4101.
  qcpWsUrl: orDefault(
    import.meta.env.VITE_QCP_WS_URL as string | undefined,
    isDev ? 'http://100.94.221.52:4101' : 'wss://qchinesepoker-ws.qlash.gg',
  ),
  // Invite REST base: gameserver HTTP origin (same host as the socket, HTTP
  // scheme). In prod, `wss://qchinesepoker-ws.qlash.gg` → HTTP origin is
  // `https://qchinesepoker-ws.qlash.gg`. In dev, direct to :4101.
  qcpInviteUrl: orDefault(
    import.meta.env.VITE_QCP_INVITE_URL as string | undefined,
    isDev ? 'http://100.94.221.52:4101' : 'https://qchinesepoker-ws.qlash.gg',
  ),
  qcpDevBypass: isDev
    ? orDefault(import.meta.env.VITE_QCP_DEV_BYPASS as string | undefined, '')
    : '',
  qcpDevUser: isDev ? orDefault(import.meta.env.VITE_QCP_DEV_USER as string | undefined, '') : '',
  // QBank — virtual currency / loyalty (.NET, owned by Gionko). SPIKE: read-only
  // wallet balance + EXP level. Dev proxies through Vite to a LOCAL QBank
  // (`http://100.94.221.52:8080`) running its internal test OIDC; override the
  // dev proxy target with VITE_QBANK_API_URL to test against prod. Prod URL is
  // wallet.qlash.gg (our QBank fork; aud = the "Qlash API Wallet" Logto resource).
  qbankApiUrl: isDev
    ? '/qbank-api'
    : orDefault(
        import.meta.env.VITE_QBANK_API_URL as string | undefined,
        'https://wallet.qlash.gg',
      ),
  // Quest (missioni & engagement). In dev il proxy Vite `/quest-api` evita la
  // CORS; in prod si parla direttamente con quest.qlash.gg.
  // NESSUN VITE_QUEST_DEV_BYPASS, di proposito: su Quest `X-Dev-Bypass` funziona
  // solo con NODE_ENV=development e la produzione gira in `production`
  // (verificato in vivo: 401 anche col bypass). Una variabile di bypass qui
  // sarebbe inerte e darebbe una falsa sicurezza a chi la trova.
  questApiUrl: isDev
    ? '/quest-api'
    : orDefault(
        import.meta.env.VITE_QUEST_API_URL as string | undefined,
        'https://quest.qlash.gg',
      ),
  // QBank LOCAL spike: the `sub` to mint an internal test-OIDC token for (against a
  // local QBank). Only used when qbankDevTestOidc is on.
  qbankDevSub: isDev
    ? orDefault(import.meta.env.VITE_QBANK_DEV_SUB as string | undefined, 'tma-dev-user')
    : '',
  // When 'true' (dev only) use QBank's INTERNAL test OIDC — local spike against a
  // local QBank. DEFAULT OFF → dev uses the REAL Logto path (e.g. dev-tma against
  // prod wallet.qlash.gg). Set VITE_QBANK_DEV_TEST_OIDC=true only for local spikes.
  qbankDevTestOidc:
    isDev && (import.meta.env.VITE_QBANK_DEV_TEST_OIDC as string | undefined) === 'true',
  // Customization microservice (banners, IAP, purchases). Mobile-app calls
  // `https://v2-api.qlash.gg/customization` directly — we proxy through Vite
  // in dev to bypass CORS, same pattern as the other backends.
  customizationApiUrl: isDev
    ? '/qlash-customization'
    : orDefault(
        import.meta.env.VITE_CUSTOMIZATION_API_URL as string | undefined,
        'https://v2-api.qlash.gg/customization',
      ),
  // Sito web pubblico QLASH (`qlash-web`) — l'origine della pagina di un contenuto
  // LEGGIBILE DA CHIUNQUE. Usata oggi da un solo consumer: il link di condivisione
  // di un articolo (`hooks/useShareArticle.ts`). Non e' un'API — non passa dal
  // browser in dev, ma finisce dentro un messaggio Telegram e lo apre il
  // destinatario: quindi NESSUN ramo `isDev`, nessun proxy Vite, stessa forma di
  // `shopUrl` qui sotto.
  //
  // Decisione di Luca 2026-09-06: il dominio e' `next.qlash.gg`, e sta IN ENV
  // proprio perche' si e' gia' mosso una volta — era `tma.qlash.gg`, cablato dentro
  // `useShareArticle`, e ogni condivisione ci e' andata a sbattere per mesi.
  //
  // Questa variabile porta la sola ORIGINE; il path dell'articolo resta scritto in
  // `useShareArticle`. ⚠️ Fino al 06/09 qui c'era scritto che quel path era
  // un'assunzione NON misurata — ed era vero quando l'ho scritto. Ora e' MISURATO,
  // e la riga si aggiorna invece di restare a mentire con l'aria della prudenza:
  // [M or53, 2026-09-06 23:2xZ — misurato sul repo `qlash-web`, non da qui]
  //   rotta `app/(public)/news/[slug]/page.tsx` (Next App Router), e `loadArticle`
  //   interroga qnews su `/qnews/${encodeURIComponent(slug)}`: e' lo SLUG che arriva
  //   all'API, letteralmente, non un id. Controlli negativi: `/news/slug-inventato`,
  //   `/news/<ObjectId>` e `/rotta-inesistente` rispondono tutti 404. Positivo:
  //   `/news/<slug>` vero -> 200 con og:title/description/image 1200x630, anche allo
  //   UA del crawler Telegram.
  //
  // 🔑 E il timore che aveva motivato la cautela — «un 200 che serve il guscio della
  // SPA e' peggio di un 404, perche' l'anteprima sembra riuscita» — su QUESTO host
  // non morde: `qlash-web` non e' una SPA ma Next server-rendered (OpenNext su
  // Cloudflare Workers), senza fallback su `index.html`, quindi un path sbagliato
  // li' da' 404 e si vede. Vale ancora, e in pieno, per `tma.qlash.gg`.
  publicSiteUrl: orDefault(
    import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined,
    'https://next.qlash.gg',
  ),
  // External web shop URL — opened in system browser via openLink when the
  // user taps "Get more banners". Real banner purchases happen in the mobile
  // app (native IAP); the Mini App stays read-only for Phase 1.
  shopUrl: orDefault(import.meta.env.VITE_SHOP_URL as string | undefined, 'https://qlash.gg/shop'),
  wsUrl: orDefault(
    import.meta.env.VITE_WS_URL as string | undefined,
    'wss://ws.tournaments.qlash.gg/connection/websocket',
  ),
  logtoEndpoint: orDefault(
    import.meta.env.VITE_LOGTO_ENDPOINT as string | undefined,
    'https://stg-account.qlash.gg/',
  ),
  logtoAppId: orDefault(
    import.meta.env.VITE_LOGTO_APP_ID as string | undefined,
    'qdaeddjw8d0jdw4r884ml',
  ),
  logtoResource: orDefault(
    import.meta.env.VITE_LOGTO_RESOURCE as string | undefined,
    'https://api.tournaments.qlash.gg',
  ),
  telegramBotUsername: orDefault(
    import.meta.env.VITE_TG_BOT_USERNAME as string | undefined,
    'QLASH_APP_bot',
  ),
  platformId: orDefault(import.meta.env.VITE_PLATFORM_ID as string | undefined, 'qlash'),
  enableEruda: import.meta.env.VITE_ENABLE_ERUDA === 'true',
  // Admin-only build gate. When `true`, AppShell shows <AccessDenied /> to any
  // authenticated user whose `is_admin !== true`. Used by the DEV bot
  // (`@QLASH_DEV_bot` at `dev-tma.qlash.gg`) to restrict access to the
  // team while PROD (`tma.qlash.gg`) keeps it disabled for everyone.
  // Default `false` so misconfiguration favors openness, not lockout.
  adminGateEnabled: import.meta.env.VITE_ADMIN_GATE_ENABLED === 'true',
  // Per-feature visibility flags. Default ON (anything but the literal string
  // `'false'` keeps the feature visible) so DEV builds without overrides keep
  // showing everything. The PROD build (`.env.production`) sets these to
  // `false` to hide features still being incubated from the public TMA at
  // `tma.qlash.gg`. Toggle to `true` when ready to ship the feature publicly.
  featureAvatarsEnabled: import.meta.env.VITE_FEATURE_AVATARS_ENABLED !== 'false',
  featureQriotsEnabled: import.meta.env.VITE_FEATURE_QRIOTS_ENABLED !== 'false',
  featureClashRoyaleEnabled: import.meta.env.VITE_FEATURE_CR_ENABLED !== 'false',
  // The AI-powered CR surfaces (deck coach + AI deck builder → qbot `/coach/*`,
  // LLM, pro-gated). Opt-in (default OFF, like the avatar AI flag), so every AI
  // entry point stays HIDDEN until we deliberately ship it — set
  // `VITE_FEATURE_CR_AI_ENABLED=true` to reveal them.
  featureCrAiEnabled: import.meta.env.VITE_FEATURE_CR_AI_ENABLED === 'true',
  featureChessEnabled: import.meta.env.VITE_FEATURE_CHESS_ENABLED !== 'false',
  featureCocEnabled: import.meta.env.VITE_FEATURE_COC_ENABLED !== 'false',
  // QGammon backgammon surface. PROD (`.env.production`) keeps this false.
  //
  // The flag is GLOBAL: on its own it limits the surface to nobody. What limits
  // it is `<RoleGate role="beta">` on the routes AND on the Hub entry
  // (routes/router.tsx + components/hub/HubSections.tsx), because a user whose
  // roles carry no scope on the QGammon resource would reach a section that
  // fails in their hands — and fails SILENTLY, the refusal arriving on the
  // socket rather than on screen.
  //
  // ⚠️ This used to say «it's MacMini-hosted + the Logto resource isn't granted
  // to the TMA app yet». Both halves were wrong: PROD has been on the OVH box
  // since 2026-07-25 — the QGammon block ~145 lines above said so already, while
  // this one contradicted it — and a first-party SPA has no per-app resource
  // grant at all: access comes from the user's ROLE. [Logto side measured by
  // qgammon-orcr on the tenant via Management API, read-only, 2026-09-01 — not
  // re-measured here.]
  featureBackgammonEnabled: import.meta.env.VITE_FEATURE_BACKGAMMON_ENABLED !== 'false',
  // QChinesePoker surface. PROD (`.env.production`) keeps this false until the game ships
  // publicly. Conditional on purpose: IF the QCP Logto resource is not granted to this app,
  // users cannot mint a QCP token and the section 401s — which is why the flag stays OFF and
  // why flipping it alone is not enough. The resource's actual state is not knowable from
  // this file: it is another system's configuration (source: the QCP front).
  featureChinesePokerEnabled: import.meta.env.VITE_FEATURE_CP_ENABLED !== 'false',
  // QBank wallet — beta soft-launch in PROD (`.env.production` keeps it `true`):
  // the Hub HUD + Profile section self-hide on 401, so only users with the
  // `qbank:wallet:read` scope (beta + admins) see a balance. Gates CurrencyHud +
  // the Profile wallet section. NOTE: the League surface has its OWN flag below —
  // keep them split so the wallet beta stays on while the league stays held.
  featureQbankEnabled: import.meta.env.VITE_FEATURE_QBANK_ENABLED !== 'false',
  // QBank League surface (Hub `LeagueCard` + the `/league` route). Split from the
  // wallet flag (2026-06-16) so PROD can keep the wallet beta ON while holding the
  // hand-seeded beta league. PROD (`.env.production`) sets this `false` until the
  // league is product-ready (scoring wired to gameplay, real names via QSocials).
  featureLeagueEnabled: import.meta.env.VITE_FEATURE_LEAGUE_ENABLED !== 'false',
  // Legacy Laravel leaderboard (`/leaderboards` + the Hub pill). Defaults ON
  // because it is LIVE in PROD today and is the platform's only per-game,
  // per-season ranking — the QDuels→QBank chain has no notion of a game and
  // never credits a TMA tournament, so switching this off loses the feature
  // rather than migrating it. The flag exists so it CAN be retired the day a
  // QSocials — livello sociale (amicizie, profili pubblici, co-watching).
  //
  // 🔴 DEFAULT ON, come `leaderboard`, perche' e' VIVO in produzione: spegnerlo di
  // default lo toglierebbe a chi ce l'ha. Il flag nasce per la ragione opposta —
  // poterlo SPEGNERE dove non deve parlare.
  //
  // ⚠️ Ed e' nato da un difetto misurato il 2026-09-05 sulla superficie di collaudo:
  // `qsocials` era **l'unico** consumer per-servizio SENZA flag (qriots, CR, chess,
  // backgammon, coc, qbank, avatar ce l'hanno tutti), e la sua URL ha come default
  // l'host di PRODUZIONE. ⇒ la build di STAGING chiamava `qsocials.qlash.gg`, cioe'
  // produzione, col token di staging.
  //
  // 🔑 Oggi ci salvava il fallimento: il token di staging non e' valido su prod, quindi
  // la chiamata moriva con 401 e il refresh con `invalid_target`. **Una protezione che
  // deriva da un errore non e' una protezione**: il giorno in cui quel token fosse
  // accettato — un tenant unificato, una resource aggiunta — un collaudo scriverebbe in
  // produzione, e sembrerebbe che funzioni.
  //
  // ⚠️ E non e' che manchi l'ambiente giusto a cui puntare: **qsocials NON HA uno
  // staging** (misurato: `stg-qsocials`, `qsocials-stg`, `staging-qsocials`,
  // `qsocials.stg` sono tutti NXDOMAIN; solo `qsocials.qlash.gg` risolve). Finche' non
  // esiste, la sola risposta corretta per una build di staging e' **non chiamarlo**.
  featureSocialEnabled: import.meta.env.VITE_FEATURE_SOCIAL_ENABLED !== 'false',

  // replacement actually produces numbers.
  featureLeaderboardEnabled: import.meta.env.VITE_FEATURE_LEADERBOARD_ENABLED !== 'false',
  // Avatar Assistant — AI prompt enhancement (Phase 2). Surfaces the "Make it
  // better with AI" / "More ideas" controls in the assistant's Review step,
  // which call qavatar-api `POST /avatars/enhance-prompt` (Gemini-text).
  // OPT-IN (default OFF, unlike the other flags): the endpoint currently lives
  // on the qavatar-api `feat/QAvatar` branch and may not be deployed to the
  // qavatar this build targets. Set VITE_FEATURE_AVATAR_AI_ENABLED=true per
  // environment only once /avatars/enhance-prompt is reachable there.
  featureAvatarAiEnabled: import.meta.env.VITE_FEATURE_AVATAR_AI_ENABLED === 'true',
  // QDuels tournaments inside the Tournaments section (open list fused into the
  // main tab, Sit&Go tab, Community tab).
  // OPT-IN (default OFF, like featureAvatarAiEnabled): it depends on qduels-api
  // shipping `GET /v1/tournaments?scope=open` (the discovery endpoint) — until
  // that is deployed, the open/Sit&Go lists 400 and Community would be the only
  // working list. The `!== 'false'` form would ship it ON everywhere including
  // PROD; this mirrors qduels' own FEATURE_TOURNAMENTS, which also defaults off.
  featureQduelsTournamentsEnabled:
    import.meta.env.VITE_FEATURE_QDUELS_TOURNAMENTS_ENABLED === 'true',
  // CR-only public launch: hides the game picker (Settings shows a fixed
  // Clash Royale badge) so the public build is single-game. OPT-IN (default
  // OFF): dev-tma stays multi-game; `.env.production` flips it to 'true' at
  // the public launch. Pairs with the CR default in useGameSelection.
  featureCrOnly: import.meta.env.VITE_FEATURE_CR_ONLY === 'true',
  // Sezione Missioni (Quest). OPT-IN (default OFF) e non `!== 'false'`, per due
  // ragioni indipendenti, ognuna sufficiente da sola:
  //  1. La resource Logto di Quest non e' ancora CONCESSA alle app TMA, quindi
  //     il token non si conia e ogni chiamata sarebbe 401.
  //  2. Per la quasi totalita' degli utenti la lista risponde 200 con array
  //     VUOTO: le istanze nascono solo per chi ha `tagVerified` (Gate F1), e il
  //     ruolo `quest-player` oggi ce l'hanno due sole utenze di collaudo.
  //     ⚠️ CORREZIONE 2026-08-30: qui c'era scritto che Quest e' "INERTE, zero
  //     eventi". NON e' piu' vero dal 28/8 e la sfumatura conta: il poller qCrC
  //     (`workers/qcrcPoll.ts`) SINTETIZZA `cr.battle` da se', non aspetta che
  //     nessuno gli pushi niente, e al 29/8 il DB di produzione porta istanze
  //     `completed`/`claimed`. «Nessun emittente contrattato» non significa
  //     «nessun dato» quando esiste un pull. (Riportato da una verifica sul DB
  //     di prod fatta da un altro cantiere il 29/8 — non da questa sessione.)
  // Accendere di default una sezione che non puo' che essere vuota sarebbe
  // peggio che non averla. Si accende quando i due blocchi si sbloccano.
  featureMissionsEnabled: import.meta.env.VITE_FEATURE_MISSIONS_ENABLED === 'true',

  // ── Limulo (BFF `app-qlash-api`) ────────────────────────────────────────────
  // La TMA e' la PRIMA superficie consumatrice reale di questo backend (ADR-0078),
  // bersaglio STAGING. Le REGOLE stanno in `lib/limulo/config.ts` — pure, quindi
  // esercitabili da `tools/verify-limulo.ts` sotto Node, dove `import.meta.env` non
  // esiste. Qui c'e' solo la lettura delle variabili, che e' il mestiere di questo file.
  //
  // ⚠️ In dev il proxy si chiama `/limulo-api` e NON `/qlash-api`, che pure e' il path
  // pubblico vero del BFF: `/qlash-api` e' gia' occupato in questa app dal monolite
  // Laravel (riga 25 + vite.config.ts). Riusare il nome «giusto» dirotterebbe dentro
  // Limulo ogni chiamata Laravel della TMA.
  limuloApiUrl: resolveLimuloApiUrl(
    isDev,
    import.meta.env.VITE_LIMULO_API_URL as string | undefined,
  ),
  // OPT-IN (`=== 'true'`), obbligatoriamente — e per COSTRUZIONE, non per lo stato del
  // backend: la sezione dipende da un'app che puo' non essere raggiungibile dove la
  // build punta, e accesa di default comparirebbe rotta in ogni build che non si ricorda
  // di spegnerla.
  // ⚠️ Lo stato dell'app `qlash-api` sull'organizzazione NON si legge da qui: e'
  // configurazione, cambia senza preavviso, e una frase in un commento diventa falsa in
  // silenzio (qui c'era scritto «oggi e' spenta» ed e' rimasto per tre giorni dopo
  // l'accensione). La fonte viva, dichiarata riga per riga col proprio grado, e' nel KB:
  // `services/qlash-limulo/identita.md`.
  // Il confronto e' scritto qui LETTERALE, non dentro una funzione: cosi' il bundler lo
  // piega a compile-time e sa gia' che la sezione e' spenta.
  featureLimuloEnabled: import.meta.env.VITE_FEATURE_LIMULO_ENABLED === 'true',
  // L'ObjectId Mongo dell'organizzazione, richiesto dal BFF al PRIMO provisioning
  // (nessuna rotta pre-auth lo espone). Viaggia solo sull'exchange-token: sul router
  // principale l'org la porta il JWT interno, e un header discordante produce un 403
  // «organization does not match the active membership» — un terzo significato di 403.
  //
  // ⚠️ NESSUN VALORE DI DEFAULT, di proposito. L'id di staging arriva da una misura fatta
  // altrove e da qui non e' verificabile: senza un external token valido ogni tentativo di
  // exchange muore prima (401), a prescindere dall'header, quindi non esiste esperimento
  // che distingua quel valore da uno sbagliato. Cablarlo come default lo farebbe viaggiare
  // in ogni build con l'aria di essere un fatto. Sta in `.env.example`, dove si legge come
  // cio' che e': una configurazione da confermare.
  limuloOrgId: orDefault(import.meta.env.VITE_LIMULO_ORG_ID as string | undefined, ''),
  // Il token esterno si incolla a mano in sviluppo (nasce da un device-flow sull'issuer
  // di staging, non da questa app).
  //
  // ⚠️ LA FORMA E' LA GARANZIA, e va lasciata esattamente com'e'. Il ternario con `isDev`
  // costante viene PIEGATO dal bundler, che elimina il ramo vero: il valore della
  // variabile non compare proprio nel file servito. Avvolgerlo in una funzione — piu'
  // leggibile, piu' testabile, apparentemente identica — rompe la garanzia, perche' il
  // bundler non puo' piegare una chiamata e ne conserva l'ARGOMENTO: il token non arriva
  // alla variabile a runtime, ma resta scritto nel JavaScript pubblico. Misurato il
  // 2026-08-30 costruendo con una sentinella e ritrovandola in `dist/assets/*.js`.
  limuloDevExternalToken: isDev
    ? orDefault(import.meta.env.VITE_LIMULO_DEV_EXTERNAL_TOKEN as string | undefined, '')
    : '',

  isDev: import.meta.env.DEV,
} as const;
